import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { CheckCircle2, Phone, MessageCircle, Home as HomeIcon, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHeader from '../components/PageHeader';
import { CONTACT } from '../data/mock';
import { trackCallClick, trackWhatsAppClick } from '../lib/analytics';

const ThankYou = () => {
  const { state } = useLocation();
  if (!state?.request_id) return <Navigate to="/book" replace />;

  const { request_id, name, pet_name, service, preferred_date, preferred_time } = state;
  const first = (name || '').split(' ')[0];
  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent(
    `Hi, I just submitted an appointment request (${request_id}) for ${pet_name || 'my pet'}${service ? ' (' + service + ')' : ''}. Can we confirm the timing?`
  )}`;

  return (
    <div className="bg-white" data-testid="thank-you-page">
      <PageHeader title="Appointment Request Received" breadcrumbs={[{ label: 'Thank You' }]} />
      <section className="py-16 lg:py-24">
        <div className="max-w-[720px] mx-auto px-5 lg:px-10 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#e870b6] to-[#5b3eb5] flex items-center justify-center mx-auto mb-6 shadow-xl">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-extrabold text-[#1a0b3d] mb-4 leading-tight">
            Appointment Request Received
          </h1>
          <p className="text-[#3b1d76]/85 text-base sm:text-lg leading-relaxed mb-3">
            Thank you{first ? `, ${first}` : ''}. We&rsquo;ve successfully received your appointment request.
          </p>
          <p className="text-[#3b1d76]/80 text-base leading-relaxed mb-8">
            Our team will review your preferred appointment time and contact you shortly to confirm your visit.
          </p>

          <div className="inline-flex flex-col items-center gap-1 px-6 py-4 rounded-2xl bg-[#fdf3fc] border border-[#f3d6f5] mb-8">
            <span className="text-[11px] tracking-[0.22em] font-bold text-[#5b3eb5]">YOUR REQUEST ID</span>
            <span data-testid="request-id" className="font-mono text-2xl sm:text-3xl font-bold text-[#1a0b3d]">{request_id}</span>
            {preferred_date && preferred_time && (
              <span className="text-[13px] text-[#3b1d76]/70 mt-1">Preferred: {preferred_date} at {preferred_time}</span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-10">
            <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('thank_you')} className="w-full sm:w-auto">
              <Button data-testid="ty-call-btn" className="w-full sm:w-auto bg-[#1a0b3d] hover:bg-[#3b1d76] text-white rounded-full px-7 py-6 font-semibold">
                <Phone className="w-4 h-4 mr-2" /> Call Clinic
              </Button>
            </a>
            <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('thank_you')} className="w-full sm:w-auto">
              <Button data-testid="ty-wa-btn" className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 font-semibold">
                <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
              </Button>
            </a>
            <Link to="/" className="w-full sm:w-auto">
              <Button data-testid="ty-home-btn" variant="outline" className="w-full sm:w-auto rounded-full border-[#5b3eb5] text-[#5b3eb5] hover:bg-[#5b3eb5] hover:text-white px-7 py-6 font-semibold">
                <HomeIcon className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>

          <div className="inline-flex items-start gap-2 text-left text-[13px] sm:text-[14px] text-[#7a4a2b] bg-[#fff7ea] border border-[#f3d9a7] rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>If this is an emergency, please call the clinic immediately at <a href={`tel:${CONTACT.phoneRaw}`} className="font-semibold underline underline-offset-2">{CONTACT.phone}</a>.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThankYou;
